import axios from "axios";
import React, { useEffect, useState } from "react";
import swal from "sweetalert";
import { getApiEndpoint } from "../../../../utils/apiConfig";
import axiosWithAuth from "../../../../utils/axiosWithAuth";

const PropertyTypeManager = () => {
  const [propertyTypeAdmin, setPropertyTypeAdmin] = useState([]);
  const [propertyType, setPropertyType] = useState({
    name: { az: "", en: "", ru: "" },
    iconType: "",
    status: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const authToken = localStorage.getItem("authToken");

  useEffect(() => {
    if (!authToken) {
      console.error("No authentication token found");
      return;
    }
    fetchPropertyTypes();
  }, []);

  const fetchPropertyTypes = () => {
    if (!authToken) return;

    axiosWithAuth()
      .get("property-types/admin")
      .then((response) => {
        setPropertyTypeAdmin(response.data);
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          console.error("Authentication failed. Please log in again.");
          localStorage.removeItem("authToken");
        }
        console.error("Error fetching property types:", error);
      });
  };

  const handleInput = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === "az" || name === "en" || name === "ru") {
      setPropertyType((prevType) => ({
        ...prevType,
        name: { ...prevType.name, [name]: value },
      }));
    } else if (type === "checkbox") {
      setPropertyType((prevType) => ({ ...prevType, [name]: checked }));
    } else {
      setPropertyType((prevType) => ({ ...prevType, [name]: value }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!authToken) {
      console.error("No authentication token found");
      return;
    }

    const apiUrl = isEditing
      ? `property-types/${editId}`
      : "property-types";

    const request = isEditing
      ? axiosWithAuth().put(apiUrl, propertyType)
      : axiosWithAuth().post(apiUrl, propertyType);

    request
      .then(() => {
        setIsEditing(false);
        setEditId(null);
        setPropertyType({
          name: { az: "", en: "", ru: "" },
          iconType: "",
          status: true,
        });
        fetchPropertyTypes();
        swal({
          title: "",
          text: "Property type has been added",
          icon: "success",
          timer: 1500,
        });
      })
      .catch((error) => {
        console.error("Error submitting property type:", error);
        swal({
          title: "",
          text: "Property type has not been added",
          icon: "error",
          timer: 1500,
        });
      });
  };

  const handleEdit = (type) => {
    setPropertyType(type);
    setEditId(type.id);
    setIsEditing(true);
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Are you sure you want to delete this property type?",
      icon: "warning",
      buttons: ["Cancel", "Yes, Delete"],
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        axiosWithAuth()
          .delete(`property-types/${id}`)
          .then(() => {
            fetchPropertyTypes();
            swal({
              title: "Success!",
              text: "Property type has been deleted successfully",
              icon: "success",
              timer: 1500,
            });
          })
          .catch((error) => {
            console.error("Error deleting property type:", error);
            swal({
              title: "Error!",
              text: "An error occurred while deleting property type",
              icon: "error",
              timer: 1500,
            });
          });
      }
    });
  };

  const handleStatusToggle = (type) => {
    const updatedType = { ...type, status: !type.status };
    
    axiosWithAuth()
      .put(
        `property-types/${type.id}`,
        updatedType
      )
      .then(() => {
        fetchPropertyTypes();
        swal({
          title: "Success!",
          text: `Property type has been ${updatedType.status ? 'activated' : 'deactivated'} successfully`,
          icon: "success",
          timer: 1500,
        });
      })
      .catch((error) => {
        console.error("Error updating property type status:", error);
        swal({
          title: "Error!",
          text: "An error occurred while updating status",
          icon: "error",
          timer: 1500,
        });
      });
  };

  return (
    <div className="property-type-manager">
      <h3 className="text-center">Property Type Manager</h3>
      <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
        <div className="property-type-name">
          <h5 className="text-center">Property Information</h5>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label" htmlFor="az">
                Azerbaijani Name
              </label>
              <input
                className="form-control"
                type="text"
                value={propertyType.name.az}
                onChange={handleInput}
                name="az"
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label" htmlFor="en">
                English Name
              </label>
              <input
                className="form-control"
                type="text"
                value={propertyType.name.en}
                onChange={handleInput}
                name="en"
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label" htmlFor="ru">
                Russian Name
              </label>
              <input
                className="form-control"
                type="text"
                value={propertyType.name.ru}
                onChange={handleInput}
                name="ru"
                required
              />
            </div>
            <div className="col-12">
              <label className="form-label" htmlFor="iconType">
                Icon Type
              </label>
              <input
                className="form-control"
                type="text"
                value={propertyType.iconType}
                onChange={handleInput}
                name="iconType"
                required
                placeholder="Enter Bootstrap icon class (e.g., bi-house)"
              />
            </div>
            <div className="col-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={propertyType.status}
                  onChange={handleInput}
                  name="status"
                  id="statusCheck"
                />
                <label className="form-check-label" htmlFor="statusCheck">
                  Active
                </label>
              </div>
            </div>
          </div>
        </div>
        <button className="btn btn-primary align-self-center">
          {isEditing ? "Save Changes" : "Add Property Type"}
        </button>
      </form>

      <div className="table-section">
        <h5 className="text-center">Property Types List</h5>
        <div className="table-responsive">
          <table className="table table-hover m-0">
            <thead>
              <tr>
                <th scope="col" className="text-center" style={{ width: "60px" }}>
                  №
                </th>
                <th scope="col">Name (az)</th>
                <th scope="col">Name (en)</th>
                <th scope="col">Name (ru)</th>
                <th scope="col">Icon Type</th>
                <th scope="col" className="text-center" style={{ width: "120px" }}>
                  Status
                </th>
                <th
                  scope="col"
                  className="text-center"
                  style={{ width: "100px" }}
                >
                  Edit
                </th>
                <th
                  scope="col"
                  className="text-center"
                  style={{ width: "100px" }}
                >
                  Delete
                </th>
              </tr>
            </thead>
            <tbody>
              {propertyTypeAdmin.map((type, index) => (
                <tr key={type.id}>
                  <th scope="row" className="text-center">
                    {index + 1}
                  </th>
                  <td>{type.name.az}</td>
                  <td>{type.name.en}</td>
                  <td>{type.name.ru}</td>
                  <td>
                    <i className={type.iconType}></i> {type.iconType}
                  </td>
                  <td className="text-center">
                    <button
                      className={`status-badge ${type.status ? 'active' : 'inactive'}`}
                      onClick={() => handleStatusToggle(type)}
                    >
                      {type.status ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-outline-warning btn-sm"
                      onClick={() => handleEdit(type)}
                    >
                      <i className="bi bi-pencil-square"></i>
                      Edit
                    </button>
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleDelete(type.id)}
                    >
                      <i className="bi bi-trash3"></i>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PropertyTypeManager;
